import React, { useEffect, useState, forwardRef } from "react";
import {
  Box,
  Grid,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Paper,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { defaultemailTemplates } from "../components/staticData";
import { generateSectionHtml } from "../components/commonFunctions";
import { formatDate } from "../components/commonComponents";
import FullPageLoader from "../components/FullPageLoader";

const EmailTemplates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDelDialog, setOpenDelDialog] = useState(false);
  const [name, setTemplateName] = useState("");
  const [delId, setDeleteId] = useState("");

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/getalltemplate`
      );
      const templates = response?.data?.data?.templates;

      if (templates) {
        setTemplates(templates);
      }
    } catch (err) {
      console.error("Error fetching templates:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCreateTemplate = () => {
    const randomId = Math.random().toString(36).slice(2);
    handleCloseCreateDialog();
    handleNavigate(randomId);
  };

  const handleCloseCreateDialog = () => {
    setOpenCreateDialog(false);
  };
  const handleCloseDelDialog = () => {
    setOpenDelDialog(false);
    setDeleteId(null);
  };

  const handleNavigate = (id) => {
    const userData = {
      id,
      name,
      type: "custom",
      subject: "",
      body: { textColor: "#000000", bgColor: "#ffffff", html: "" },
    };
    navigate(`/email-template-editor/${id}`, { state: userData });
  };
  const handleDelete = async (id) => {
    console.log("id", id);
    if (!id) return;

    try {
      handleCloseDelDialog();
      setLoading(true);
      await axios.delete(`${process.env.REACT_APP_API_URL}/deletetemplate`, {
        data: { id },
      });

      await fetchTemplates();
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <FullPageLoader />}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        pt={3}
        px={4}
      >
        <Typography variant="h6">Email Templates</Typography>

        <Button
          className="button"
          variant="contained"
          color="primary"
          disableElevation
          onClick={() => setOpenCreateDialog(true)}
        >
          Create template
        </Button>
      </Stack>

      <Grid
        container
        spacing={3}
        padding="30px"
        className="email-templates-wrapper"
      >
        {[...Object.entries(templates || {})].map(([id, templateObj]) => {
          // Pick default if it exists and no custom
          // const selected =
          //   templateObj?.type === "custom"
          //     ? templateObj
          //     : templates?.[id] || templateObj;
          const selected = templateObj;

          const { html } = selected?.body || {};
          const name = selected?.name;
          const body = selected?.body;

          return (
            <Grid
              item
              size={{ xs: 6, sm: 6, md: 4, l: 3, xl: 3 }}
              key={id}
              className="email-preview"
            >
              <Paper
                elevation={2}
                sx={{
                  borderRadius: "14px",
                  overflow: "hidden",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  background: "#fff",
                  border: "1px solid #EEEEEE",
                  boxShadow: "none",
                }}
              >
                <Box
                  sx={{
                    padding: "25px 20px 0",
                    flexGrow: 1,
                    background: "#fafafa",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "260px",
                      overflow: "hidden",
                      borderRadius: "10px 10px 0 0",
                      boxShadow: "0px 0px 7px 0px #00000012",
                      background: "#fff",
                    }}
                  >
                    {loading ? (
                      <CircularProgress />
                    ) : html ? (
                      <PreviewPanel html={generateSectionHtml(body)} />
                    ) : (
                      <Typography color="text.secondary">
                        ✏️ No preview available.{" "}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Footer */}

                <Box
                  sx={{
                    padding: "18px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Stack>
                    <Typography
                      fontWeight={600}
                      fontSize="14px"
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {name}
                    </Typography>
                    <Typography
                      fontSize="12px"
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      Last Updated on&nbsp;
                      {formatDate(selected?.updatedAt, "small")}
                    </Typography>
                  </Stack>
                  <IconButton
                    size="medium"
                    sx={{
                      backgroundColor: "#F3F4F6",
                      color: "black",
                      "&:hover": {
                        backgroundColor: "#e5e6e8",
                      },
                    }}
                    onClick={() =>
                      navigate(`/email-template-editor/${id}`, {
                        state: selected,
                      })
                    }
                  >
                    <Edit sx={{ fontSize: 18 }} />
                  </IconButton>
                  {templateObj?.type == "custom" && (
                    <IconButton
                      size="medium"
                      sx={{
                        backgroundColor: "#F3F4F6",
                        color: "black",
                        "&:hover": {
                          backgroundColor: "#e5e6e8",
                        },
                      }}
                      onClick={() => {
                        setOpenDelDialog(true);
                        setDeleteId(id);
                      }}
                    >
                      <Delete sx={{ fontSize: 18 }} />
                    </IconButton>
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Dialog
        key="create-modal"
        open={openCreateDialog}
        keepMounted
        onClose={handleCloseCreateDialog}
        aria-describedby="create-modal-description"
      >
        <DialogTitle>Create New Template</DialogTitle>

        <DialogContent sx={{ mt: 1 }}>
          <DialogContentText id="create-modal-description" sx={{ mb: 1 }}>
            Please enter template details.
          </DialogContentText>

          <TextField
            label="Template Name"
            size="small"
            fullWidth
            required
            value={name}
            onChange={(e) => setTemplateName(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button
            size="small"
            variant="outlined"
            onClick={handleCloseCreateDialog}
          >
            Cancel
          </Button>

          <Button
            size="small"
            variant="contained"
            onClick={handleSubmitCreateTemplate}
            disabled={!name}
          >
            Next
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        key="delete-modal"
        open={openDelDialog}
        keepMounted
        onClose={handleCloseDelDialog}
        aria-describedby="del-dialog-description"
      >
        <DialogTitle>{"Delete this template?"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="del-dialog-description">
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCloseDelDialog}>
            Cancle
          </Button>
          <Button
            variant="contained"
            color="critical"
            onClick={() => handleDelete(delId)}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const PreviewPanel = ({ html }) => {
  return <div className="preview" dangerouslySetInnerHTML={{ __html: html }} />;
};

export default EmailTemplates;
